import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  ConversationType,
  selectMetadataById,
  selectUserById,
  selectUnreadCountById,
  PrivateConversationMetadata,
  GroupConversationMetadata,
  TwilioMessage,
  ConversationTypeHandlerBuilder,
  UserReachabilityStatus,
} from 'models/twilio';
import { selectLocale } from 'models/localization';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from 'components/twilio/translations';
import { renderTimeText } from '../utils/renderTime';
import metadataEquality from '../utils/metadataEquality';
import { IMAGES } from '../constants';
import useTwilioMessage from './useTwilioMessage';
import useFirebaseChatConfig from './useFirebaseChatConfig';
import usePublicChatroomName from './usePublicChatroomName';

interface ListItemData {
  displayName: string | null;
  messagePreview: string;
  iconSrc?: string;
  reachabilityStatus?: UserReachabilityStatus;
}

type ConversationTypeHandler = ListItemData & {
  unreadCount: number;
  timeString: string;
  isLoading: boolean;
}

const useConversationListItem = (conversationSid: string) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const { getMessagePreview, prefixSelf } = useTwilioMessage();
  const { boothChatroomDisabled, hideChatMillis } = useFirebaseChatConfig();
  const metadata = useSelector(selectMetadataById(conversationSid), metadataEquality({
    otherUserId: (prev, next) => shallowEqual(prev, next),
    conversationName: (prev, next) => shallowEqual(prev, next),
    uniqueName: (prev, next) => shallowEqual(prev, next),
    lastMessage: (prevMessage: TwilioMessage, nextMessage: TwilioMessage) => prevMessage?.id === nextMessage?.id,
  }));
  const publicChatroomName = usePublicChatroomName(metadata.uniqueName);
  const { lastMessage, conversationType } = metadata;
  const otherUser = useSelector(selectUserById((metadata as PrivateConversationMetadata).otherUserId));
  const listItemData = useMemo((): ConversationTypeHandlerBuilder<ListItemData> => {
    const previewContent = getMessagePreview(lastMessage);
    return {
      [ConversationType.PRIVATE]: {
        displayName: otherUser?.name || conversationSid,
        messagePreview: prefixSelf(previewContent, lastMessage?.author),
        reachabilityStatus: otherUser?.reachabilityStatus ?? UserReachabilityStatus.OFFLINE,
      },
      [ConversationType.GROUP]: {
        displayName: (metadata as GroupConversationMetadata).conversationName || conversationSid,
        messagePreview: boothChatroomDisabled ? null : prefixSelf(previewContent, lastMessage?.author),
        iconSrc: IMAGES.GROUP_CHAT,
      },
      [ConversationType.ANNOUNCEMENT]: {
        displayName: t(...TRANSLATIONS.JOINED_ROOMS.ANNOUNCEMENT),
        messagePreview: previewContent,
        iconSrc: IMAGES.ANNOUNCEMENT,
      },
      [ConversationType.PUBLIC]: {
        displayName: publicChatroomName,
        messagePreview: previewContent,
        iconSrc: IMAGES.GROUP_CHAT,
      },
      [ConversationType.UNKNOWN]: {
        displayName: null,
        messagePreview: null,
      },
    };
  }, [
    boothChatroomDisabled,
    conversationSid,
    getMessagePreview,
    lastMessage,
    metadata,
    otherUser,
    prefixSelf,
    publicChatroomName,
    t,
  ]);

  const unreadCount = useSelector(selectUnreadCountById(conversationSid));

  const timeString = useMemo(() => {
    if (!lastMessage || lastMessage.dateCreated.getTime() < hideChatMillis) return null;
    return renderTimeText(lastMessage.dateCreated, t, locale);
  }, [lastMessage, hideChatMillis, t, locale]);

  return useMemo(() => ({
    ...listItemData[conversationType],
    unreadCount,
    timeString,
    isLoading: metadata.isLoading,
  } as ConversationTypeHandler), [
    listItemData,
    conversationType,
    unreadCount,
    timeString,
    metadata.isLoading,
  ]);
};

export default useConversationListItem;
